import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowUpRight, Search, Clock, Sparkles } from "lucide-react";
import { listPublishedPosts } from "@/lib/blog-public.functions";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { BLOG_TOPICS, topicHaystack } from "@/data/blog-topics";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";
import { PageHeader } from "./fordon";
import { AdSlot } from "@/components/AdSlot";

const BLOG_URL = "https://www.akhusbilfritid.se/blogg";

type PostCard = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_description: string | null;
  cover_url: string | null;
  published_at: string | null;
  reading_minutes: number | null;
  keywords: string[] | null;
  focus_keyword: string | null;
};

const TOPICS = BLOG_TOPICS;

function haystack(p: PostCard) {
  return topicHaystack(p);
}


function formatDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });
}

export const Route = createFileRoute("/blogg/")({
  loader: async () => {
    const { posts } = await listPublishedPosts();
    return { posts: (posts ?? []) as PostCard[] };
  },
  head: ({ loaderData }) => {
    const posts = loaderData?.posts ?? [];
    const count = posts.length;
    const title = `Husbilsblogg – ${count > 0 ? `${count} guider, ` : ""}tips & tester | AK Husbil & Fritid`;
    const description =
      "Husbilsblogg från Kalix: köpguider, vinterförvaring, ställplatser i Norrbotten, reservdelstips och ärliga tester. 20 års erfarenhet — läs innan du köper.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content:
            "husbilsblogg, husvagnsblogg, ställplatser Norrbotten, husbil tips, husvagn guide, vinterförvaring husbil, köpa husbil, Rapido",
        },
        { property: "og:type", content: "website" },
        { property: "og:title", content: "Husbilsblogg – Guider & tips | AK Husbil & Fritid" },
        { property: "og:description", content: description },
        { property: "og:url", content: BLOG_URL },
        { property: "og:site_name", content: "AK Husbil & Fritid" },
        { property: "og:locale", content: "sv_SE" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(posts[0]?.cover_url
          ? [
              { property: "og:image", content: posts[0].cover_url as string },
              { name: "twitter:image", content: posts[0].cover_url as string },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: BLOG_URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Blog",
                "@id": `${BLOG_URL}#blog`,
                name: "AK Husbil & Fritid – Blogg",
                description,
                url: BLOG_URL,
                inLanguage: "sv-SE",
                publisher: {
                  "@type": "Organization",
                  name: "AK Husbil & Fritid",
                  url: "https://www.akhusbilfritid.se",
                },
              },
              {
                "@type": "ItemList",
                name: "Artiklar",
                numberOfItems: posts.length,
                itemListElement: posts.slice(0, 30).map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `${BLOG_URL}/${p.slug}`,
                  name: p.title,
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
                  { "@type": "ListItem", position: 2, name: "Blogg", item: BLOG_URL },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: BloggPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-3xl">Kunde inte ladda bloggen</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function BloggPage() {
  const { posts } = Route.useLoaderData() as { posts: PostCard[] };
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState<string | null>(null);

  const activeTopics = useMemo(
    () => TOPICS.filter((t) => posts.some((p) => t.re.test(haystack(p)))),
    [posts],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts.filter((p) => {
      const hay = haystack(p).toLowerCase();
      if (needle && !hay.includes(needle)) return false;
      if (topic) {
        const t = TOPICS.find((x) => x.id === topic);
        if (t && !t.re.test(hay)) return false;
      }
      return true;
    });
  }, [posts, q, topic]);

  const isDefaultView = !q.trim() && !topic;
  const featured = isDefaultView ? filtered[0] : undefined;
  const rest = featured ? filtered.slice(1) : filtered;
  const totalMinutes = posts.reduce((n, p) => n + (p.reading_minutes ?? 6), 0);

  return (
    <>
      <PageHeader
        label="Blogg · Kunskap från branschen"
        title={
          <>
            Råd från <span className="font-italic-display text-accent">erfarna</span>.
          </>
        }
        sub="20 års erfarenhet sammanfattat i guider, checklistor och ärliga tester — det vi själva hade velat veta innan vi köpte vår första husbil."
      />

      <section className="container-edge pt-10 md:pt-14">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-wider text-muted-foreground">
          <span className="inline-flex items-center gap-2 text-accent">
            <Sparkles size={13} /> {posts.length} artiklar
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock size={13} /> ~{Math.round(totalMinutes / 60)} h läsning totalt
          </span>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <label className="relative block">
            <Search
              size={18}
              className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sök bland guiderna – t.ex. vinterförvaring, solceller, körkort…"
              aria-label="Sök i bloggen"
              className="w-full rounded-full border border-input bg-card py-4 pl-13 pr-5 text-[15px] outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
              style={{ paddingLeft: "3.25rem" }}
            />
          </label>

          <div className="-mx-4 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
            <div className="flex w-max gap-2 md:w-auto md:flex-wrap">
              <button
                type="button"
                onClick={() => setTopic(null)}
                aria-pressed={topic === null}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm transition ${
                  topic === null
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card hover:bg-secondary"
                }`}
              >
                Allt
              </button>
              {activeTopics.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTopic(topic === t.id ? null : t.id)}
                  aria-pressed={topic === t.id}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm transition ${
                    topic === t.id
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card hover:bg-secondary"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span className="text-xs uppercase tracking-wider">Läs ämnesvis:</span>
            {activeTopics.map((t) => (
              <Link
                key={t.id}
                to="/blogg/amne/$topic"
                params={{ topic: t.id }}
                className="underline decoration-border underline-offset-4 transition hover:text-accent hover:decoration-accent"
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>


      <div className="container-edge">
        <AdSlot variant="display-horizontal" label="Annons" className="my-0 pt-6" />
      </div>

      <section className="container-edge py-12 md:py-16">
        {posts.length === 0 && (
          <div className="rounded-3xl border border-border bg-card p-12 text-center">
            <p className="eyebrow">Status</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl">
              Inga inlägg <span className="font-italic-display text-accent">publicerade</span> än.
            </h2>
            <p className="mt-3 text-muted-foreground">Kom snart tillbaka — vi laddar artiklar.</p>
          </div>
        )}

        {posts.length > 0 && filtered.length === 0 && (
          <div className="rounded-3xl border border-border bg-card p-10 text-center">
            <h2 className="font-display text-2xl md:text-3xl">
              Inget träffade <span className="font-italic-display text-accent">“{q}”</span>.
            </h2>
            <button
              type="button"
              onClick={() => {
                setQ("");
                setTopic(null);
              }}
              className="mt-5 rounded-full border border-border bg-background px-5 py-2.5 text-sm hover:bg-secondary"
            >
              Rensa filter
            </button>
          </div>
        )}

        {featured && (
          <Link
            to="/blogg/$slug"
            params={{ slug: featured.slug }}
            className="group mb-10 grid overflow-hidden rounded-3xl border border-border bg-card hover-lift md:mb-14 md:grid-cols-2"
          >
            <div className="aspect-[16/10] overflow-hidden md:aspect-auto md:h-full">
              {featured.cover_url ? (
                <img
                  src={featured.cover_url}
                  alt={featured.title}
                  width={1200}
                  height={750}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                />

              ) : (
                <div className="h-full w-full bg-secondary" />
              )}
            </div>
            <div className="flex flex-col justify-center p-7 md:p-12">
              <p className="eyebrow text-accent">Senast publicerat</p>
              <h2 className="mt-4 font-display text-3xl leading-[1.05] md:text-5xl">{featured.title}</h2>
              {(featured.excerpt || featured.meta_description) && (
                <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground md:text-lg">
                  {featured.excerpt ?? featured.meta_description}
                </p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-wider text-muted-foreground">
                {featured.published_at && <time dateTime={featured.published_at}>{formatDate(featured.published_at)}</time>}
                {featured.reading_minutes ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{featured.reading_minutes} min</span>
                  </>
                ) : null}
              </div>
              <span className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition group-hover:bg-primary/90">
                Läs artikeln <ArrowUpRight size={16} />
              </span>
            </div>
          </Link>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <Link
              key={p.id}
              to="/blogg/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card hover-lift"
            >
              {p.cover_url ? (
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.cover_url}
                    srcSet={imgSrcSet(p.cover_url)}
                    sizes={CARD_SIZES}
                    alt={p.title}
                    width={800}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="aspect-[16/10] bg-secondary" />
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-accent">
                    {p.published_at ? new Date(p.published_at).toLocaleDateString("sv-SE") : "Guide"}
                    {p.reading_minutes ? ` · ${p.reading_minutes} min` : ""}
                  </p>
                  <ArrowUpRight
                    size={18}
                    className="shrink-0 text-muted-foreground transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground"
                  />
                </div>
                <h2 className="mt-3 font-display text-2xl leading-tight">{p.title}</h2>
                {(p.excerpt || p.meta_description) && (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {p.excerpt ?? p.meta_description}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>

        {posts.length > 0 && (
          <div className="mt-16">
            <NewsletterSignup
              variant="inline"
              source="blog-index"
              title="Nya guider direkt i inkorgen"
              description="Få våra bästa husbils- och husvagnsguider plus nya fordon i lager. ~1 mejl/månad."
            />
          </div>
        )}
      </section>
    </>
  );
}
